// TICKET-01726 (2026-10-06): AI scribes such as Vetnio hand the vet ONE note with headed
// sections (Anamnese, Klinisk undersøkelse, Diagnose, ...). When such a note is pasted
// into a journal field we fan the sections out to the matching journal text fields,
// verbatim and without AI. Pure functions; the DOM wiring lives in newJournal.js.

// Heading synonyms per journal text field. Lowercase, trimmed, no trailing colon.
export const JOURNAL_NOTE_HEADINGS = {
    chiefComplaint: [
        'anamnese', 'sykehistorie', 'bakgrunn', 'årsak til besøk', 'henvendelsesårsak', 'subjektivt',
        'chief complaint', 'history', 'subjective',
    ],
    generalCondition: ['allmenntilstand', 'almenntilstand', 'generell tilstand', 'general condition'],
    physicalExaminationFindings: [
        'klinisk undersøkelse', 'undersøkelse', 'status', 'kliniske funn', 'funn', 'objektivt', 'klinisk status',
        'physical examination', 'physical examination findings', 'examination', 'objective',
    ],
    diagnosticTests: [
        'diagnostikk', 'diagnostiske tester', 'prøver', 'prøvesvar', 'undersøkelser', 'laboratorieprøver',
        'labprøver', 'lab', 'diagnostic tests', 'diagnostics',
    ],
    diagnosis: ['diagnose', 'diagnoser', 'tentativ diagnose', 'vurdering', 'diagnosis', 'assessment'],
    treatmentPlan: ['behandling', 'behandlingsplan', 'plan', 'tiltak', 'treatment', 'treatment plan'],
    medicines: ['medisiner', 'medikamenter', 'legemidler', 'medisinering', 'medication', 'medications'],
    clientCommunication: [
        'kommunikasjon med eier', 'informasjon til eier', 'eierkommunikasjon', 'samtale med eier',
        'client communication',
    ],
    recommendations: ['anbefalinger', 'anbefaling', 'råd', 'recommendations'],
    followUp: ['oppfølging', 'videre oppfølging', 'kontroll', 'follow-up', 'follow up'],
};

const JOURNAL_TEXT_FIELDS = Object.keys(JOURNAL_NOTE_HEADINGS);

const HEADING_LOOKUP = JOURNAL_TEXT_FIELDS.reduce((acc, field) => {
    JOURNAL_NOTE_HEADINGS[field].forEach((synonym) => { acc[synonym] = field; });
    return acc;
}, {});

// A line that is nothing but a heading: optional markdown '#', optional bold, optional colon.
const HEADING_LINE = /^\s*(?:#{1,6}\s+)?(?:\*\*|__)?\s*([^*_:#]{2,40}?)\s*(?:\*\*|__)?\s*:?\s*$/;
// "Heading: text on the same line".
const INLINE_HEADING = /^\s*(?:#{1,6}\s+)?(?:\*\*|__)?\s*([^*_:#]{2,40}?)\s*(?:\*\*|__)?\s*:\s*(\S.*)$/;

const normalizeHeading = s => s.toLowerCase().replace(/\s+/g, ' ').trim();

// TICKET-01878: scribes qualify headings ("Klinisk undersøkelse / palpasjon", "Diagnose (tentativ)",
// "Behandling - plan"). A slash, parenthesis, comma, semicolon, pipe, or a spaced dash ends the
// heading proper; "follow-up" style hyphens without spaces are left alone.
const HEADING_QUALIFIER = /\s*[/(,;|]\s*|\s+[-–—]\s+/;

function lookupHeading(raw) {
    const heading = normalizeHeading(raw);
    const exact = HEADING_LOOKUP[heading];
    if (exact) {
        return exact;
    }
    const [head] = heading.split(HEADING_QUALIFIER);
    return (head && head !== heading) ? HEADING_LOOKUP[head.trim()] : undefined;
}

function matchHeading(line) {
    let m = HEADING_LINE.exec(line);
    if (m) {
        const field = lookupHeading(m[1]);
        return field ? { field, rest: '' } : null;
    }
    m = INLINE_HEADING.exec(line);
    if (m) {
        const field = lookupHeading(m[1]);
        return field ? { field, rest: m[2].trim() } : null;
    }
    return null;
}

/**
 * Split a headed note into journal fields.
 * Returns { fields: { <journalField>: text }, preamble } or null when fewer than two
 * known headings are found (then the paste should go through untouched).
 */
export function splitJournalNote(text) {
    if (typeof text !== 'string' || !text.trim()) {
        return null;
    }
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    const preamble = [];
    const chunks = []; // [{ field, lines }]
    let current = null;
    let matched = 0;
    lines.forEach((line) => {
        const heading = matchHeading(line);
        if (heading) {
            matched += 1;
            current = { field: heading.field, lines: heading.rest ? [heading.rest] : [] };
            chunks.push(current);
            return;
        }
        if (current) {
            current.lines.push(line);
        } else {
            preamble.push(line);
        }
    });
    if (matched < 2) {
        return null;
    }
    const fields = {};
    chunks.forEach((chunk) => {
        const body = chunk.lines.join('\n').trim();
        if (!body) {
            return;
        }
        fields[chunk.field] = fields[chunk.field] ? `${fields[chunk.field]}\n\n${body}` : body;
    });
    return { fields, preamble: preamble.join('\n').trim() };
}

const appendText = (existing, addition) => {
    const base = typeof existing === 'string' ? existing.trim() : '';
    return base ? `${base}\n\n${addition}` : addition;
};

/**
 * Work out the new value per journal field for a pasted note.
 * The preamble (text before the first heading) goes to the field the vet pasted into
 * (chiefComplaint when that is not a journal text field). Existing field text is kept
 * and the new section appended after a blank line. Returns null when the text does
 * not split.
 */
export function planJournalPaste(text, journal, targetField) {
    const split = splitJournalNote(text);
    if (!split) {
        return null;
    }
    const target = JOURNAL_TEXT_FIELDS.includes(targetField) ? targetField : 'chiefComplaint';
    const parts = { ...split.fields };
    if (split.preamble) {
        parts[target] = parts[target] ? `${split.preamble}\n\n${parts[target]}` : split.preamble;
    }
    const plan = {};
    Object.keys(parts).forEach((field) => {
        plan[field] = appendText(journal ? journal[field] : '', parts[field]);
    });
    return plan;
}

export default { splitJournalNote, planJournalPaste, JOURNAL_NOTE_HEADINGS };
